import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Progress } from '@backstage/core-components';
import { useApi, identityApiRef } from '@backstage/core-plugin-api';
import { useOktaAuthGuard } from './useOktaAuthGuard';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated, isGuest, isLoading } = useOktaAuthGuard();
  const identityApi = useApi(identityApiRef);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleGuestAccess = async () => {
      if (!isLoading && isGuest && isAuthenticated === false) {
        try {
          // Sign out the guest user
          await identityApi.signOut();

          // Store the intended destination
          sessionStorage.setItem(
            'backstage.auth.redirectAfterLogin',
            location.pathname + location.search + location.hash,
          );

          // Reload to trigger Okta authentication
          // The App.tsx SignInPage logic will handle showing the Okta login
          window.location.reload();
        } catch (error) {
          // Force reload anyway to attempt re-authentication
          window.location.reload();
        }
      }
    };

    handleGuestAccess();
  }, [isLoading, isGuest, isAuthenticated, identityApi, location, navigate]);

  // Show loading state while checking authentication
  if (isLoading) {
    return <Progress />;
  }

  // If guest user is detected, show loading while signing out
  if (isGuest) {
    return <Progress />;
  }

  // If not authenticated with Okta, show loading (will trigger sign-in flow)
  if (isAuthenticated === false) {
    return <Progress />;
  }

  // User is authenticated with Okta, render the protected content
  return <>{children}</>;
};
